/**
 * A round of structural edits, held until the user commits them.
 *
 * `quotation.apply_revisions` BUMPS THE REVISION and reopens the quotation as a draft on every
 * call. Applying each edit as it is made would turn one round of customer corrections into
 * revisions #1, #2 and #3 — and the revision number would stop meaning "what the customer was
 * sent", which is the only thing it is for.
 *
 * So qty/add/remove collect here and go out as ONE call. Price edits do NOT come through this
 * module: `update_quotation_line` bumps nothing, so there is nothing to batch.
 *
 * Pure and immutable — the screen holds a `PendingEdit[]` in React state and replaces it.
 */

import { prevailingSellRatio } from './quotationCommission'
import { freeSerials } from './uomChoice'
import type { RevisionChange, RevisionRequest } from './types'

export interface PendingEdit {
  item_code: string
  op: 'set_qty' | 'remove' | 'add'
  qty: number
  uom?: string
  /**
   * The supplier's unit cost, when one was recorded for this item while queueing the add.
   *
   * `apply_revisions` has no price field and does NOT source one: measured live 2026-09-04 on
   * QTN-2026-00014, adding `1903010001` produced a line at cost 0.00 even though SQ-2026-00278
   * already quoted that exact item on that exact enquiry row. So an added line arrives at zero
   * and the purchaser had to go and find the price by hand — search the item, open its supplier
   * history, "use this price", apply. This carries it through instead.
   *
   * Applied AFTER the add, as a `cost_unit` PATCH — see `pricedAddsFor`.
   */
  cost?: number
  /**
   * What `cost` is denominated in — the supplier's currency for a recorded quotation, the
   * company's for stock and contract prices. Absent means the company's. `pricedAddsFor` converts
   * into the quotation's currency before the write, because the quotation line's cost must be in
   * the currency the document is in (`quotationCurrency.ts`, 2026-09-14).
   */
  cost_currency?: string
  /**
   * The customer's own words for an ADD — the request's `detail`. `apply_revisions` creates the
   * line with no description (D29); `describedAddsFor` writes this onto it afterwards so the line
   * prints what was asked for, not a bare item code.
   */
  description?: string
  /**
   * The serial number the added line takes (Akhil, 2026-09-15): a number freed by a removal in
   * the same round, or the next one. `apply_revisions` appends the line unnumbered; this is
   * written onto it afterwards (`describedAddsFor`) and the table orders by it.
   */
  position?: string
  /**
   * Where `cost` came from — `'Provision'` for a blanket-order rate, `'Supplier'` for a recorded
   * quotation. Written to `Quotation Line.source`, which is what the route badge reads
   * (`tagFromSource`); without it a contract-priced line showed as `RFQ`, reported 2026-09-05.
   */
  source?: string
  /**
   * Who the line is bought from, and the supplier quotation it was recorded on (Akhil,
   * 2026-09-16: the PO is raised from the quotation line). Written by `setLineCost` after the
   * apply — `apply_revisions` itself has no supplier field.
   */
  supplier?: string
  supplier_quotation?: string
}

export function emptyBatch(): PendingEdit[] {
  return []
}

/**
 * Adds an edit, REPLACING any existing one for the same line.
 *
 * Typing 2 then 3 into one quantity box is a single intention. Keeping both would send two
 * contradictory ops for one item in the same apply, and the server would be entitled to honour
 * either.
 */
export function queueEdit(batch: readonly PendingEdit[], edit: PendingEdit): PendingEdit[] {
  const rest = batch.filter((e) => e.item_code !== edit.item_code)
  return [...rest, edit]
}

export function discardEdit(batch: readonly PendingEdit[], itemCode: string): PendingEdit[] {
  return batch.filter((e) => e.item_code !== itemCode)
}

/**
 * The lines on a quotation that carry `itemCode` — usually one, but never assumed (fix round,
 * final review: I5). `queueEdit` above REPLACES any existing edit for the same `item_code`
 * because it is keyed by nothing else, and `apply_revisions` itself resolves a wire change the
 * same way — so when more than one line shares an `item_code`, a qty/remove edit for it cannot be
 * expressed at all: it would resolve against every line sharing the code, not the one a purchaser
 * meant. A caller must check `.length > 1` here BEFORE calling `queueEdit`, the same way the
 * reprice path already refuses the identical ambiguity before calling `update_quotation_line`
 * (`RevisionsPage.tsx`'s `applyRequest`).
 */
export function linesForItemCode<T extends { item_code: string }>(lines: readonly T[], itemCode: string): T[] {
  return lines.filter((l) => l.item_code === itemCode)
}

/** The wire shape `apply_revisions` takes. `uom` is omitted rather than sent empty. */
export function toChanges(batch: readonly PendingEdit[]): RevisionChange[] {
  return batch.map((e) =>
    e.uom ? { op: e.op, item_code: e.item_code, qty: e.qty, uom: e.uom } : { op: e.op, item_code: e.item_code, qty: e.qty },
  )
}

export function describeBatch(batch: readonly PendingEdit[]): string {
  if (batch.length === 0) return 'No changes'
  return `Apply ${batch.length} change${batch.length === 1 ? '' : 's'}`
}

/**
 * Turns a customer's revision REQUEST into the queued edit that resolves it. This is the ONLY
 * path from "resolve this request" to a change — the request table and the embedded quotation's
 * own qty/remove controls both funnel through here into the SAME batch, so a purchaser working
 * the requests list one row at a time still produces one revision, not one per row.
 *
 * `reprice` returns `null` on purpose: it has no `apply_revisions` op at all (this module's own
 * header) — `update_quotation_line` bumps nothing, so a reprice is resolved immediately by the
 * caller, outside the batch, rather than queued here. A caller must check for `reprice` before
 * calling this, exactly as it must supply a real `item_code`.
 */
export function editFromRequest(
  request: Pick<RevisionRequest, 'type' | 'item_code' | 'qty'> & { detail?: string },
  currentQty: number | null,
): PendingEdit | null {
  if (!request.item_code) return null

  switch (request.type) {
    case 'reprice':
      return null
    case 'remove':
      return { item_code: request.item_code, op: 'remove', qty: 0 }
    case 'add': {
      const detail = (request.detail ?? '').trim()
      return { item_code: request.item_code, op: 'add', qty: request.qty ?? 1, ...(detail ? { description: detail } : {}) }
    }
    case 'set_qty':
      return { item_code: request.item_code, op: 'set_qty', qty: request.qty ?? currentQty ?? 0 }
  }
}

/**
 * The lines an apply just created that still need their recorded supplier price written on.
 *
 * `apply_revisions` returns only `{revision, status}` — no line ids — so the created line has to
 * be found by `item_code` against the refetched quotation. That is the same key the batch and the
 * wire already resolve changes by (this module's header), with the same ambiguity: an item on more
 * than one line cannot be matched to the right one, so it is SKIPPED rather than guessed at.
 *
 * Zero is not a price. A supplier who quoted nothing is not offering free goods —
 * `manualSupplierQuotation.ts` refuses to file a zero rate for exactly this reason, and a zero
 * that slipped through here would win every allocation it touched.
 */
/**
 * The wording each add should carry, paired with the line the apply created — same pairing rule
 * as `pricedAddsFor` (exactly one new line for the item, else nothing). `item_name` comes from the
 * item master where the caller has it, so the customer's copy prints a name rather than a code.
 */
export interface DescribedAdd {
  line_id: string
  description?: string
  item_name?: string
  /** The serial number to write as `enquiry_row_no` / `row_no`. */
  position?: string
}

export function describedAddsFor(
  batch: readonly PendingEdit[],
  lines: readonly { line_id: string; item_code: string }[],
  nameByCode: ReadonlyMap<string, string>,
): DescribedAdd[] {
  const out: DescribedAdd[] = []
  for (const edit of batch) {
    if (edit.op !== 'add') continue
    const matches = linesForItemCode(lines, edit.item_code)
    if (matches.length !== 1) continue
    const item_name = edit.description ? nameByCode.get(edit.item_code) : undefined
    const row: DescribedAdd = { line_id: matches[0].line_id }
    if (edit.description) row.description = edit.description
    if (item_name) row.item_name = item_name
    if (edit.position) row.position = edit.position
    if (!row.description && !row.item_name && !row.position) continue
    out.push(row)
  }
  return out
}

export interface PricedAdd {
  line_id: string
  cost: number
  source?: string
  /** The PO side's WHO — see `PendingEdit.supplier`. */
  supplier?: string
  supplier_quotation?: string
  /** The PO side — see `useQuotation.ts`'s `LinePoSide`. Absent when no currency could be named. */
  po?: { po_currency: string; po_currency_rate: number; conversion_rate: number }
}

export function pricedAddsFor(
  batch: readonly PendingEdit[],
  lines: readonly { line_id: string; item_code: string }[],
  /**
   * The rate from a price's currency (`''` for the company's) into the QUOTATION's currency, or
   * `null` when none is on file. Defaults to par, which is right only for a same-currency
   * quotation — every real caller passes one.
   */
  toQuoteCurrency: (from: string) => number | null = () => 1,
  /** What an edit with no stated currency is in — the company's. Names the PO side. */
  companyCurrency = '',
): PricedAdd[] {
  const out: PricedAdd[] = []
  for (const edit of batch) {
    if (edit.op !== 'add') continue
    const cost = edit.cost
    const priced = cost !== undefined && Number.isFinite(cost) && cost > 0
    // An unpriced add still names its supplier (2026-09-16): the cost is the server's to find,
    // the WHO is not.
    if (!priced && !edit.supplier) continue
    const matches = linesForItemCode(lines, edit.item_code)
    // Not created, or created more than once — neither can be priced without guessing.
    if (matches.length !== 1) continue
    // A price this cannot convert is left unwritten — the line stays at 0.00 and says so —
    // rather than written at par as though the currencies were the same.
    if (!priced) {
      out.push({ line_id: matches[0].line_id, cost: 0, source: edit.source, supplier: edit.supplier as string, ...(edit.supplier_quotation ? { supplier_quotation: edit.supplier_quotation } : {}) })
      continue
    }
    const rate = toQuoteCurrency(edit.cost_currency ?? '')
    if (rate === null || !Number.isFinite(rate) || rate <= 0) continue
    const poCurrency = edit.cost_currency || companyCurrency
    out.push({
      line_id: matches[0].line_id,
      cost: Math.round((cost as number) * rate * 100) / 100,
      source: edit.source,
      ...(edit.supplier ? { supplier: edit.supplier } : {}),
      ...(edit.supplier_quotation ? { supplier_quotation: edit.supplier_quotation } : {}),
      // The supplier's own figure and currency, kept beside the converted cost (Akhil,
      // 2026-09-15) — only when a currency can be named for it.
      ...(poCurrency ? { po: { po_currency: poCurrency, po_currency_rate: cost as number, conversion_rate: rate } } : {}),
    })
  }
  return out
}

/**
 * One queued change, in the words the pending-changes list shows.
 *
 * A batch is invisible until it is applied — the quotation table renders the pending rows, but a
 * row that merely LOOKS like the others tells nobody what is about to happen to it, and the only
 * other signal was a button reading "Apply 1 change". So the purchaser could not see what they had
 * queued, which is what "we can't see what changes are done" was.
 *
 * A quantity change reads as its DESTINATION ("to 3"), never as a delta: `set_qty` carries the
 * final quantity and `queueEdit` replaces rather than accumulates, so "+1" would be a lie the
 * second time the same box is typed in.
 */
export function describeEdit(edit: PendingEdit): { action: string; detail: string } {
  switch (edit.op) {
    case 'add':
      return { action: 'Add', detail: edit.uom ? `${edit.qty} ${edit.uom}` : `${edit.qty}` }
    case 'set_qty':
      return { action: 'Change qty', detail: `to ${edit.qty}` }
    case 'remove':
      return { action: 'Remove', detail: '' }
  }
}

/**
 * Why an add cannot be queued for this item, or `null` when it can.
 *
 * `apply_revisions` APPENDS: adding an item that is already quoted produces a SECOND line for the
 * same code rather than changing the first. Measured on QTN-2026-00014, where `1903010001` ended
 * up on `8kb8hghvit` (cost 92.00) and `ol41vjkpdf` (cost 0.00).
 *
 * That breaks two things at once. Every edit in this module is keyed by `item_code` alone — the
 * wire resolves changes the same way — so a repeated code makes qty and remove unexpressible
 * (`linesForItemCode`'s note, and the "not supported for a repeated item_code" message). And
 * `pricedAddsFor` refuses to guess which of the two a recorded price belongs to, so the new line
 * keeps the 0.00 it was created with. Both were reported as one bug.
 *
 * Refused at the point of queueing instead, where the purchaser can still do the thing they
 * actually meant — change the existing line's quantity.
 */
export function addBlockedReason(
  lines: readonly { line_id: string; item_code: string }[],
  itemCode: string,
): string | null {
  const matches = linesForItemCode(lines, itemCode)
  if (matches.length === 0) return null
  return (
    `${itemCode} is already on this quotation (line ${matches.map((l) => l.line_id).join(', ')}). ` +
    `Adding it again would create a second line for the same item — change that line's quantity instead.`
  )
}

export { prevailingSellRatio }

/**
 * Where an added line can sit (Akhil, 2026-09-15): the number of any line removed in this same
 * round — so a replacement takes the replaced line's place — and the next number after every
 * line on the quotation and every add already queued. Non-numeric serials are counted, not
 * parsed, so an odd row never blocks the next number.
 */
export function positionOptions(
  lines: readonly { line_id: string; item_code: string; cust_sno: string }[],
  batch: readonly PendingEdit[],
): { value: string; label: string }[] {
  const taken = batch.filter((e) => e.op === 'add' && e.position).map((e) => e.position as string)
  const removed = new Set(batch.filter((e) => e.op === 'remove').map((e) => e.item_code))
  // A line queued for removal frees its number for this round; a line removed in an earlier
  // round is already a gap in the numbering. Both come first — replacing is why lines are added.
  const staying = lines.filter((l) => !removed.has(l.item_code)).map((l) => l.cust_sno)
  const free = freeSerials(staying, taken)
  const last = free[free.length - 1]
  return free.map((n) => ({ value: n, label: n === last ? `${n} — at the end` : `${n} — takes the removed line’s place` }))
}
